import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  Link,
  ListSubheader,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  User as UserIcon,
  Users as UsersIcon,
  Bookmark as BookMarkIcon,
  Radio as StreamIcon
} from 'react-feather';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useAuth from 'src/hooks/useAuth';
import Logo from 'src/components/Logo';
import useAppInfo from 'src/hooks/useAppInfo';
import useAbility from 'src/hooks/useAbility';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import { HomeWork } from '@material-ui/icons';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PaymentOutlinedIcon from '@material-ui/icons/PaymentOutlined';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import NavItem from './NavItem';

const ALL_NAV_ITEMS = [
  {
    subheader: 'Reports',
    items: [
      {
        href: '/app/dashboard',
        icon: BarChartIcon,
        title: 'Dashboard'
      }
    ]
  },
  {
    subheader: 'Management',
    items: [
      {
        href: '/app/customers',
        icon: UsersIcon,
        title: 'Students',
        requiredAction: 'searchStudents'
      },
      {
        href: '/app/admin',
        icon: UsersIcon,
        title: 'Pay Admin',
        requiredAction: 'adminAccount'
      },
      {
        href: '/app/teachers',
        icon: UsersIcon,
        title: 'Teachers',
        requiredAction: 'searchTeachers'
      },
      {
        href: '/app/courses',
        icon: OndemandVideoIcon,
        title: 'Courses',
        requiredAction: 'searchCourses'
      },
      {
        href: '/app/tutes',
        icon: DescriptionOutlinedIcon,
        title: 'Tutes',
        requiredAction: 'searchTutes'
      },
      {
        href: '/app/live-streams',
        icon: StreamIcon,
        title: 'Streams',
        requiredAction: 'searchAdminLessons'
      },
      {
        href: '/app/subjects',
        icon: BookMarkIcon,
        title: 'Subjects',
        requiredAction: 'getSubject'
      },
      {
        href: '/app/management/invoices',
        icon: AttachMoneyIcon,
        title: 'Invoices',
        requiredAction: 'searchPeymentRecipts'
      },
      {
        href: '/app/management/account',
        icon: UsersIcon,
        title: 'Admins',
        requiredAction: 'searchAdminAccounts'
      },
      {
        href: '/app/account',
        icon: UserIcon,
        title: 'Account'
      },
      {
        href: '/app/settings',
        icon: SettingsIcon,
        title: 'Settings',
        requiredAction: 'updateSysInfo'
      }
    ]
  },
  {
    subheader: 'Courier',
    items: [
      {
        href: '/app/courier/reports',
        icon: ListAltOutlinedIcon,
        title: 'Reports',
        requiredAction: 'getRunScriptCurrierReport'
      },
      {
        href: '/app/courier/labels',
        icon: LocalShippingOutlinedIcon,
        title: 'Labels',
        requiredAction: 'searchCourierJobs'
      },
      {
        href: '/app/courier/tracking',
        icon: LocalShippingOutlinedIcon,
        title: 'Tracking',
        requiredAction: 'searchCourierRecords'
      },
    ]
  },
  {
    subheader: 'Exam Center',
    items: [
      {
        exact: true,
        href: '/app/exam-center/multi-course-payments',
        icon: PaymentOutlinedIcon,
        title: 'Multi Course Payments',
        requiredAction: 'viewMultiCoursePaymentStatuses'
      }
    ]
  }
];

const exam_sections = [
  {
    subheader: 'Management',
    items: [
      {
        href: '/app/customers',
        icon: UsersIcon,
        title: 'Students'
      },
    ],
  },
  {
    subheader: 'Exam Center',
    items: [
      {
        exact: true,
        href: '/app/exam-center',
        icon: HomeWork,
        title: 'Exam Center'
      },
      {
        exact: true,
        href: '/app/exam-center/multi-course-payments',
        icon: PaymentOutlinedIcon,
        title: 'Multi Course Payments',
        requiredAction: 'viewMultiCoursePaymentStatuses'
      }
    ]
  },
  {
    subheader: 'Reports',
    items: [
      {
        href: '/app/reports/commission',
        icon: BarChartIcon,
        title: 'Commission Report',
        requiredAction: 'runScriptCommissionReport'
      },
      {
        href: '/app/reports/student',
        icon: BarChartIcon,
        title: 'Student Report',
        requiredAction: 'runStudentReport'
      }
    ]
  },

];

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({
          acc, item, pathname, depth
        }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc, pathname, item, depth
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        exact={item.exact}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();
  const { appInfo } = useAppInfo();
  const ability = useAbility();

  const getFilteredNavItems = (sections) => {
    return sections.map(section => {
      const filteredItems = section.items.filter(item => {
        if (item.requiredAction) {
          if (Array.isArray(item.requiredAction)) {
            return ability && item.requiredAction.some(action => ability.can(action, 'all'));
          }
          return ability && ability.can(item.requiredAction, 'all');
        }
        return true;
      });
      return {
        ...section,
        items: filteredItems
      };
    }).filter(section => section.items.length > 0);
  };

  const currentSections = getFilteredNavItems(ALL_NAV_ITEMS);
  const currentExamSections = getFilteredNavItems(exam_sections);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/app/account">
              <Avatar
                alt="User"
                className={classes.avatar}
                src={
                  user.avatar_url
                    ? "https://api.ewings.lk/" + user.avatar_url_new
                    : ''
                }
              />
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {`${user.fname} ${user.lname}`}
            </Link>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {user.role === 'EXAM_CENTER' ? (
            currentExamSections.map(section => (
              <List
                key={section.subheader}
                subheader={
                  <ListSubheader disableGutters disableSticky>
                    {section.subheader}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: section.items,
                  pathname: location.pathname
                })}
              </List>
            ))
          ) : currentSections.map((section) => (
            <List
              key={section.subheader}
              subheader={(
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
        <Divider />
        {/* <Box p={2}>
          <Box p={2} borderRadius="borderRadius" bgcolor="background.dark">
            <Typography variant="h6" color="textPrimary">
              Developed by Axoten Innovation
            </Typography>
          </Box>
        </Box> */}
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
